import { inject, Injectable } from '@angular/core';
import { catchError, Observable, tap } from 'rxjs';
import { LoginRequest } from '@app/@shared/models/login-request.model';
import { LoginResponse } from '../models/login-response.model';
import { ApplicationProfile, PortalTheme } from '../models/application-profile.model';
import { ChangePassword } from '../models/change-password.model';
import { ResetPasswordRequest } from '../models/reset-password-request.model';
import { Credentials } from '../models/credentials.model';
import { Resources } from '../models/constants';
import { ApiResponse } from '../models/api-response.model';
import { BaseApiService } from '@ep/shared';
import { Employee } from '../models/employee.model';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { HydrationActions } from '@app/resources/ngrx/actions';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService extends BaseApiService {
  private readonly store = inject(Store);

  login(loginRequest: LoginRequest): Observable<LoginResponse> {
    const json = JSON.stringify(loginRequest);
    return this.post<LoginResponse>('/v2/Security/Login', json);
  }

  loginWithTc(loginRequest: LoginRequest): Observable<LoginResponse> {
    this.store.dispatch(HydrationActions.clear());
    const json = JSON.stringify(loginRequest);
    return this.post<LoginResponse>('/v2/Security/LoginWithTC', json);
  }

  logout(): Observable<void> {
    return this.get<void>('/v2/Security/Logout', undefined);
  }

  forgotPassword(forgotPasswordRequest: ResetPasswordRequest): Observable<void> {
    const json = JSON.stringify(forgotPasswordRequest);
    return this.post<void>('/v2/Security/ForgotPassword', json);
  }

  getApplicationProfile(): Observable<ApplicationProfile> {
    return this.get<ApplicationProfile>('/v2/ApplicationProfile/employee').pipe(
      catchError((err) => {
        console.error('Issue with getting employee profile');
        throw err;
      }),
      map(mapToApplicationProfile)
    );
  }

  changePassword(changePassword: ChangePassword): Observable<void> {
    const json = JSON.stringify(changePassword);
    return this.post<void>('/v2/Security/ChangePassword', json);
  }

  confirmPasswordReset(confirmPasswordReset: ChangePassword): Observable<void> {
    const json = JSON.stringify(confirmPasswordReset);
    return this.post<void>('/v2/Security/ConfirmPasswordReset', json);
  }

  getEmployeeByPasswordResetId(passwordResetId: string): Observable<ApiResponse<Employee>> {
    return this.get<{ $values?: any[] }>(`/v2/Security/PasswordReset/${passwordResetId}`).pipe(
      map((response) => {
        const collection = (response?.$values ?? []).map(mapToEmployee);
        return {
          Collection: collection,
          PageNumber: 0,
          DisplayCount: collection.length,
          TotalRecordCount: collection.length,
          ErrorCode: 0,
          ErrorMessage: [],
          ErrorMessageCodes: [],
          InvalidDatas: [],
          ResponseType: 0,
          ResponseMessages: [],
          Errors: null,
        } as ApiResponse<Employee>;
      })
    );
  }
}

function mapToEmployee(employee: any): Employee {
  return {
    $id: employee.$id,
    EmployeeId: employee.employeeId,
    MerchantId: employee.merchantId ?? 0,
    NameFirst: employee.nameFirst,
    NameLast: employee.nameLast,
    AccessType: employee.accessType,
    StatusType: employee.statusType ?? 0,
    LanguageType: employee.languageType ?? 0,
    EmployeeLocations: [],
    EmailAddress: employee.emailAddress,
  };
}

function mapToApplicationProfile(profile: any): ApplicationProfile {
  return {
    Employee: {
      $id: profile.employee.$id,
      EmployeeId: profile.employee.employeeId,
      NameFirst: profile.employee.nameFirst,
      NameLast: profile.employee.nameLast,
      AccessType: profile.employee.accessType,
    },
    Merchant: {
      MerchantId: profile.merchant.merchantId,
      Name: profile.merchant.name,
      StatusType: profile.merchant.statusType ?? 0,
      LoyaltyProgram: {
        LoyaltyProgramId: profile.merchant.loyaltyProgram.loyaltyProgramId,
        Name: profile.merchant.loyaltyProgram.name,
        LoyaltyProgramType: profile.merchant.loyaltyProgram.loyaltyProgramType,
        ReceiveAmount: profile.merchant.loyaltyProgram.receiveAmount,
      },
    },
    Location: {
      LocationId: profile.location.locationId,
      Name: profile.location.name,
      Address: profile.location.address,
      UtcOffsetHour: profile.location.utcOffsetHour,
      PhoneNumber: profile.location.phoneNumber,
      TimeZoneType: profile.location.timeZoneType,
    },
    PortalTheme: {
      Theme: mapToApplicationProfilePortalTheme(profile.portalTheme.theme),
      PrimaryLogo: profile.portalTheme.primaryLogo,
      SecondaryLogo: profile.portalTheme.secondaryLogo,
      FavIcon: profile.portalTheme.favIcon,
    },
  };
}

export function mapToApplicationProfilePortalTheme(value: string | any): PortalTheme {
  if (value === null || value === undefined || value === '' || value === 'Default') {
    return {
      primary: '#3b8cc7',
      backgroundColor: '#eff3f9',
      defaultTextColor: '#607389',
      sidebar: {
        backgroundColor: {
          primary: '#213246',
          secondary: '#30a0e6',
          active: '#3b8cc7',
          dropdown: '#3b8cc7ba',
        },
        textColor: '#FFF',
        logoBackgroundColor: '#1e2235',
      },
      header: {
        primary: '#3b8cc7',
        backgroundColor: '#dbe4f0',
        itemsColor: '#3b8cc7',
        textColor: '#fff',
      },
      dashboard: {
        coloredCardTextColor: '#fff',
        sales: '#30a0e6',
        loyalty: '#68ac3d',
        rewards: '#ad6dd1',
        reviews: '#3a5171',
        cardBackgroundColor: '#fff',
        headerBackgroundColor: '#dbe4f0',
        headerTextColor: '#3b8cc7',
      },
      table: {
        header: {
          backgroundColor: '#3b8cc7',
          textColor: '#fff',
        },
        subHeaderBackgroundColor: '#f8f9fa',
        subHeaderTextColor: '#000',
        statusColors: {
          primary: '#30a0e6',
          secondary: '#1e2235',
          warning: '#ffd340',
          danger: '#E71D36',
        },
        body: {
          primaryBackgroundColor: '#fff',
          secondaryBackgroundColor: '#fcfcfc',
          textColor: '#000',
        },
      },
      form: {
        header: {
          primaryBackgroundColor: '#3b8cc7',
          secondaryBackgroundColor: '#FFF',
          textColor: '#fff',
          itemsColor: '#3b8cc7',
          itemsTextColor: '#fff',
        },
        backgroundColor: '#fff',
      },
    };
  }

  const themeData = typeof value === 'string' ? JSON.parse(value) : value;
  return {
    primary: themeData.primary,
    backgroundColor: themeData.backgroundColor,
    defaultTextColor: themeData.defaultTextColor,
    sidebar: {
      backgroundColor: {
        primary: themeData.sidebar.backgroundColor.primary,
        secondary: themeData.sidebar.backgroundColor.secondary,
        active: themeData.sidebar.backgroundColor.active,
        dropdown: themeData.sidebar.backgroundColor.dropdown,
      },
      textColor: themeData.sidebar.textColor,
      logoBackgroundColor: themeData.sidebar.logoBackgroundColor,
    },
    header: {
      primary: themeData.header.primary,
      backgroundColor: themeData.header.backgroundColor,
      itemsColor: themeData.header.itemsColor,
      textColor: themeData.header.textColor,
    },
    dashboard: {
      coloredCardTextColor: themeData.dashboard.coloredCardTextColor,
      sales: themeData.dashboard.sales,
      loyalty: themeData.dashboard.loyalty,
      rewards: themeData.dashboard.rewards,
      reviews: themeData.dashboard.reviews,
      cardBackgroundColor: themeData.dashboard.cardBackgroundColor,
      headerBackgroundColor: themeData.dashboard.headerBackgroundColor,
      headerTextColor: themeData.dashboard.headerTextColor,
    },
    table: {
      header: {
        backgroundColor: themeData.table.header.backgroundColor,
        textColor: themeData.table.header.textColor,
      },
      subHeaderBackgroundColor: themeData.table.subHeaderBackgroundColor,
      subHeaderTextColor: themeData.table.subHeaderTextColor,
      statusColors: {
        primary: themeData.table.statusColors.primary,
        secondary: themeData.table.statusColors.secondary,
        warning: themeData.table.statusColors.warning,
        danger: themeData.table.statusColors.danger,
      },
      body: {
        primaryBackgroundColor: themeData.table.body.primaryBackgroundColor,
        secondaryBackgroundColor: themeData.table.body.secondaryBackgroundColor,
        textColor: themeData.table.body.textColor,
      },
    },
    form: {
      header: {
        primaryBackgroundColor: themeData.form.header.primaryBackgroundColor,
        secondaryBackgroundColor: themeData.form.header.secondaryBackgroundColor,
        textColor: themeData.form.header.textColor,
        itemsColor: themeData.form.header.itemsColor,
        itemsTextColor: themeData.form.header.itemsTextColor,
      },
      backgroundColor: themeData.form.backgroundColor,
    },
  };
}
